import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { apiClient, type MetaConfig } from '../utils/api';
import { useTelegram } from '../hooks/useTelegram';

const transferSchema = z.object({
  fromAccount: z.string().min(1, 'Счет списания обязателен'),
  toAccount: z.string().min(1, 'Счет зачисления обязателен'),
  amount: z.number({ required_error: 'Сумма обязательна' }).min(0.01, 'Сумма должна быть больше 0'),
  fee: z.number({ required_error: 'Комиссия обязательна' }).min(0, 'Комиссия не может быть отрицательной'),
  fromCurrency: z.string().min(1, 'Валюта счета списания обязательна'),
  toCurrency: z.string().min(1, 'Валюта счета зачисления обязательна'),
  executor: z.string().min(1, 'Исполнитель обязателен'),
  transaction_time: z.string().min(1, 'Время транзакции обязательно'),
  note: z.string().optional(),
}).refine((data) => data.fromAccount !== data.toAccount, {
  message: 'Счета списания и зачисления должны отличаться',
  path: ['toAccount'],
});

type TransferFormData = z.infer<typeof transferSchema>;

interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
}


export function TransferForm({ onSuccess, onCancel }: Props) {
  const [meta, setMeta] = useState<MetaConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { tg } = useTelegram();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<TransferFormData>({
    resolver: zodResolver(transferSchema),
    defaultValues: {
      fromAccount: '',
      toAccount: '',
      amount: 0,
      fee: 0,
      fromCurrency: 'USD',
      toCurrency: 'USD',
      executor: '',
      transaction_time: dayjs().format('YYYY-MM-DDTHH:mm'),
      note: '',
    },
  });

  const watchedFromAccount = watch('fromAccount');
  const watchedToAccount = watch('toAccount');

  useEffect(() => {
    loadMeta();
  }, []);

  useEffect(() => {
    if (tg) {
      tg.MainButton.setText('Выполнить перевод');
      tg.MainButton.onClick(handleMainButtonClick);
      tg.MainButton.show();

      return () => {
        tg.MainButton.offClick(handleMainButtonClick);
        tg.MainButton.hide();
      };
    }
  }, [tg]);

  const loadMeta = async () => {
    try {
      const data = await apiClient.getMeta();
      setMeta(data);
    } catch (error) {
      console.error('Failed to load meta:', error);
    }
  };

  const handleMainButtonClick = () => {
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (data: TransferFormData) => {
    setIsLoading(true);
    try {
      // Создаем транзакцию вывода (списания)
      const debitTransaction = {
        name: `Вывод с ${data.fromAccount} на ${data.toAccount}`,
        money: -Math.abs(data.amount), // Вывод: только основная сумма
        fee: data.fee, // Комиссия указывается отдельно
        currency: data.fromCurrency,
        account: data.fromAccount,
        executor: data.executor,
        transaction_time: data.transaction_time,
        destination_time: data.transaction_time,
        category: 'move',
        payment: '',
        note: data.note || `Вывод с ${data.fromAccount} на ${data.toAccount}`,
      };

      // Создаем транзакцию прихода (зачисления)
      const creditTransaction = {
        name: `Приход на ${data.toAccount} с ${data.fromAccount}`,
        money: Math.abs(data.amount), // Приход: только основная сумма без комиссии
        fee: 0, // Комиссия только при выводе
        currency: data.toCurrency,
        account: data.toAccount,
        executor: data.executor,
        transaction_time: data.transaction_time,
        destination_time: data.transaction_time,
        category: 'move',
        payment: '',
        note: data.note || `Приход на ${data.toAccount} с ${data.fromAccount}`,
      };

      // Создаем обе транзакции
      await Promise.all([
        apiClient.createTransaction(debitTransaction),
        apiClient.createTransaction(creditTransaction)
      ]);

      // Показываем анимацию успеха
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        reset();
        onSuccess?.();
      }, 2500);

    } catch (error) {
      console.error('Error saving transfer:', error);
      alert(`Ошибка: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!meta) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-telegram-hint">Загрузка...</p>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="p-6 text-center">
        <div className="mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <span className="text-2xl">✅</span>
          </div>
          <h3 className="text-xl font-semibold text-blue-600 mb-2">Перевод выполнен!</h3>
          <p className="text-telegram-hint">Две транзакции успешно созданы</p>
        </div>
      </div>
    );
  }

  // Получаем доступные счета для "Куда", исключая выбранный счет "Откуда"
  const availableToAccounts = meta.accounts.filter(account => account !== watchedFromAccount);

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <span className="text-xl">🔄</span>
        </div>
        <h2 className="text-xl font-bold text-telegram-text mb-1">Перевод между счетами</h2>
        <p className="text-sm text-telegram-hint">Внутренняя операция</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">


        {/* Счета */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Счет списания */}
            <div>
              <label className="block text-sm font-medium text-telegram-text mb-2">
                Откуда (списание)
              </label>
              <select
                {...register('fromAccount')}
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Выберите счет</option>
                {meta.accounts.map((account) => (
                  <option key={account} value={account}>
                    {account}
                  </option>
                ))}
              </select>
              {errors.fromAccount && <p className="text-red-500 text-xs mt-1">{errors.fromAccount.message}</p>}
            </div>

            {/* Счет зачисления */}
            <div>
              <label className="block text-sm font-medium text-telegram-text mb-2">
                Куда (зачисление)
              </label>
              <select
                {...register('toAccount')}
                className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                disabled={!watchedFromAccount}
              >
                <option value="">Выберите счет</option>
                {availableToAccounts.map((account) => (
                  <option key={account} value={account}>
                    {account}
                  </option>
                ))}
              </select>
              {errors.toAccount && <p className="text-red-500 text-xs mt-1">{errors.toAccount.message}</p>}
            </div>
          </div>

          {/* Визуальный индикатор направления */}
          {watchedFromAccount && watchedToAccount && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 animate-fadeIn">
              <div className="flex items-center justify-between">
                <div className="flex-1 text-center">
                  <div className="font-medium text-blue-700">{watchedFromAccount}</div>
                  <div className="text-xs text-blue-600">Списание</div>
                </div>
                <div className="mx-4">
                  <div className="flex items-center">
                    <div className="w-8 h-0.5 bg-blue-500"></div>
                    <div className="text-blue-500 text-xl">→</div>
                    <div className="w-8 h-0.5 bg-blue-500"></div>
                  </div>
                </div>
                <div className="flex-1 text-center">
                  <div className="font-medium text-blue-700">{watchedToAccount}</div>
                  <div className="text-xs text-blue-600">Зачисление</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Сумма перевода */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Сумма перевода
          </label>
          <div className="relative">
            <input
              {...register('amount', { valueAsNumber: true })}
              type="number"
              step="0.01"
              min="0"
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg font-semibold"
              placeholder="0.00"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600 font-medium">
              {watch('fromCurrency')}
            </div>
          </div>
          {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount.message}</p>}
        </div>

        {/* Комиссия */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Комиссия за перевод <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              {...register('fee', { valueAsNumber: true })}
              type="number"
              step="0.01"
              min="0"
              className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="0.00"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
              {watch('fromCurrency')}
            </div>
          </div>
          <p className="text-xs text-telegram-hint mt-1">
            Банковские или системные комиссии за операцию
          </p>
          {errors.fee && <p className="text-red-500 text-xs mt-1">{errors.fee.message}</p>}
        </div>


        {/* Валюты счетов */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Валюта счета списания */}
          <div>
            <label className="block text-sm font-medium text-telegram-text mb-2">
              Валюта счета списания
            </label>
            <div className="grid grid-cols-2 gap-2">
              {meta.currencies.map((currency) => (
                <button
                  key={currency}
                  type="button"
                  onClick={() => setValue('fromCurrency', currency)}
                  className={`
                    p-3 rounded-lg border transition-all duration-200 font-medium
                    ${watch('fromCurrency') === currency
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {currency}
                </button>
              ))}
            </div>
            {errors.fromCurrency && <p className="text-red-500 text-xs mt-1">{errors.fromCurrency.message}</p>}
          </div>

          {/* Валюта счета зачисления */}
          <div>
            <label className="block text-sm font-medium text-telegram-text mb-2">
              Валюта счета зачисления
            </label>
            <div className="grid grid-cols-2 gap-2">
              {meta.currencies.map((currency) => (
                <button
                  key={currency}
                  type="button"
                  onClick={() => setValue('toCurrency', currency)}
                  className={`
                    p-3 rounded-lg border transition-all duration-200 font-medium
                    ${watch('toCurrency') === currency
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {currency}
                </button>
              ))}
            </div>
            {errors.toCurrency && <p className="text-red-500 text-xs mt-1">{errors.toCurrency.message}</p>}
          </div>
        </div>

        {/* Исполнитель */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Исполнитель
          </label>
          <select
            {...register('executor')}
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            <option value="">Выберите исполнителя</option>
            {meta.executors.map((executor) => (
              <option key={executor} value={executor}>
                {executor}
              </option>
            ))}
          </select>
          {errors.executor && <p className="text-red-500 text-xs mt-1">{errors.executor.message}</p>}
        </div>

        {/* Время */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Время операции
          </label>
          <input
            {...register('transaction_time')}
            type="datetime-local"
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
          {errors.transaction_time && (
            <p className="text-red-500 text-xs mt-1">{errors.transaction_time.message}</p>
          )}
        </div>

        {/* Примечание */}
        <div>
          <label className="block text-sm font-medium text-telegram-text mb-2">
            Примечание <span className="text-gray-400">(необязательно)</span>
          </label>
          <textarea
            {...register('note')}
            className="w-full p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
            rows={3}
            placeholder="Цель перевода или дополнительная информация"
          />
        </div>

        {/* Кнопки */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-blue-500 text-white py-4 px-6 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-blue-600 active:scale-95"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Выполнение...
              </div>
            ) : (
              '🔄 Выполнить перевод'
            )}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-4 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 active:scale-95 transition-all"
            >
              Отмена
            </button>
          )}
        </div>
      </form>
    </div>
  );
}